import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AddMoreWords.css';
import { backendUrl } from '../config';
import Spinner from './Spinner';
import OnScreenKeyboard from './OnScreenKeyboard';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { supabase } from '../supabaseClient';
import { isTextSafe } from '../utils/safeForAds';
import { getUserTimezone } from '../utils/timezoneUtils';
import { useModuleData } from '../hooks/useModuleData';
import { useWordsData } from '../hooks/useWordsData';

const AddMoreWords = ({ moduleId, groupId, groupData, onWordsAdded, refetchUsage }) => {
  const { t, isRTL: isAppRTL } = useLanguage();
  const { moduleData, loading: moduleLoading } = useModuleData(moduleId); // moduleId is now the position
  const { words } = useWordsData(groupId);

  const [activeTab, setActiveTab] = useState('manual');
  const [pairs, setPairs] = useState([{ left: '', right: '' }]);
  const [isAddingWithAI, setIsAddingWithAI] = useState(false);
  const [error, setError] = useState('');
  const [aiWordCount, setAiWordCount] = useState(25);

  // Keyboard state
  const [keyboardArmedLang, setKeyboardArmedLang] = useState(null);
  const [keyboardActiveIdx, setKeyboardActiveIdx] = useState(null);
  const [focusedInputIdx, setFocusedInputIdx] = useState(null);
  const keyboardOverlayRef = useRef(null);
  const rightInputRefs = useRef([]);
  const requestIdRef = useRef(0);

  // Don't render until module data is loaded
  if (moduleLoading || !moduleData || !groupData) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>{t('loading') || 'Loading...'}</div>;
  }



  const handlePairChange = (index, side, value) => {
    const newPairs = [...pairs];
    // Limit toLang words (right side) to 150 characters
    if (side === 'right') {
      newPairs[index][side] = value.substring(0, 150);
    } else {
      newPairs[index][side] = value;
    }
    setPairs(newPairs);
  };

  const handleAddPair = () => {
    setPairs([...pairs, { left: '', right: '' }]);
  };

  const handleRemovePair = (index) => {
    const newPairs = pairs.filter((_, i) => i !== index);
    setPairs(newPairs);
    // Reset keyboard state if the active input was removed
    if (keyboardActiveIdx === index) {
      setKeyboardActiveIdx(null);
      setKeyboardArmedLang(null);
    }
  };

  const handleManualAdd = () => {
    setError('');
    const filledPairs = pairs.filter(p => p.left.trim() && p.right.trim());

    if (filledPairs.length === 0) {
      setError(t('pleaseAddAtLeastOne'));
      return;
    }

    // Check for inappropriate content in all word pairs
    for (const pair of filledPairs) {
      if (!isTextSafe(pair.left) || !isTextSafe(pair.right)) {
        setError(t('inappropriateContentDetected'));
        return;
      }
    }

    // Keep the two sides explicit: language-keyed objects collapse when both
    // sides use the same language (definition modules).
    const newWords = filledPairs.map(p => ({ left: p.left, right: p.right }));
    onWordsAdded(newWords);
    setPairs([{ left: '', right: '' }]);
  };

  const handleAiAdd = async () => {
    setError('');

    // Extra safety check
    if (!moduleData || !groupData) {
      setError(t('dataNotLoaded'));
      return;
    }

    // Get the current user's access token
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    // Check word cap before showing spinner
    const checkRes = await fetch(`${backendUrl}/usage/check-words`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessToken,
        wordCount: aiWordCount,
        timezone: getUserTimezone(),
        // Pass classroomId so the check uses classroom limits instead of personal limits
        classroomId: moduleData.classroom_id || null,
        moduleId: moduleData.id
      })
    });
    const checkData = await checkRes.json();
    if (!checkData.canGenerate) {
      setError(checkData.error || t('failedToFetchWords'));
      return;
    }
    setIsAddingWithAI(true);
    const currentRequestId = ++requestIdRef.current;
    try {
      // Ensure exclusion list is a flat array of non-empty strings in the target language
      // Defensive: log and handle undefined words

      let existingWords = [];
      if (Array.isArray(words)) {
        existingWords = words.map(w => w.to_text).filter(word => typeof word === 'string' && word.trim());
      }

      const res = await fetch(`${backendUrl}/gemini-generate-words`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: groupData.name,
          fromLang: moduleData.from_lang,
          toLang: moduleData.to_lang,
          wordCount: aiWordCount,
          dialect: moduleData.dialect,
          existingWords: existingWords,
          accessToken,
          timezone: getUserTimezone(),
          // Pass moduleId and classroom_id for security verification
          // Backend will verify module belongs to classroom before using classroom budget
          moduleId: moduleData.id,
          classroomId: moduleData.classroom_id || null,
        }),
      });
      const data = await res.json();
      if (requestIdRef.current === currentRequestId) {
        if (Array.isArray(data.words) && data.words.length > 0) {
          // Normalize AI pairs to match manual add format
          const normalized = data.words.map(pair => ({
            [moduleData.from_lang]: pair[moduleData.from_lang],
            [moduleData.to_lang]: pair[moduleData.to_lang]
          }));
          onWordsAdded(normalized);
          if (refetchUsage) refetchUsage();
        } else if (data.error) {
          setError(data.error);
        } else {
          setError(t('failedToFetchWords'));
        }
      }
    } catch (err) {
      if (requestIdRef.current === currentRequestId) {
        setError(err.message);
      }
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setIsAddingWithAI(false);
      }
    }
  };

  return (
    <>
      {isAddingWithAI && <Spinner message={t('generatingWords')} size="large" overlay />}
      <div className="add-more-words-container">
        <div className="add-words-tabs">
          <button
            className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
          >
            {t('addWordsManually')}
          </button>
          {moduleData.from_lang !== moduleData.to_lang && (
            <button
              className={`tab-button ${activeTab === 'ai' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              {t('generateWithAI')}
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'manual' && (
            <div className="manual-add-section">
              <div className="word-pairs-section">
                <div className="language-direction-header">
                  <span>{moduleData.from_lang === moduleData.to_lang ? t('term') : t(moduleData.from_lang)}</span>
                  <span>{moduleData.from_lang === moduleData.to_lang ? t('definition') : t(moduleData.to_lang)}</span>
                </div>
                {pairs.map((pair, index) => {
                  const isSourceRTL = isRTLanguage(getKeyboardLangCode(moduleData.from_lang));
                  const isTargetRTL = isRTLanguage(getKeyboardLangCode(moduleData.to_lang));
                  return (
                    <div key={index} className="word-pair-row">
                      <input
                        type="text"
                        placeholder={moduleData.from_lang === moduleData.to_lang ? t('term') : `${t(moduleData.from_lang)} ${t('word')}`}
                        value={pair.left}
                        onChange={(e) => handlePairChange(index, 'left', e.target.value)}
                        style={{
                          direction: isSourceRTL ? 'rtl' : 'ltr',
                          textAlign: isSourceRTL ? 'right' : 'left'
                        }}
                        className={isSourceRTL ? 'placeholder-rtl' : 'placeholder-ltr'}
                      />
                      <span className="arrow-separator">{isAppRTL ? '←' : '→'}</span>
                      <div className="input-with-keyboard-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder={moduleData.from_lang === moduleData.to_lang ? t('definition') : `${t(moduleData.to_lang)} ${t('translation')}`}
                          value={pair.right}
                          onChange={(e) => handlePairChange(index, 'right', e.target.value)}
                          maxLength={150}
                          style={{
                            direction: isTargetRTL ? 'rtl' : 'ltr',
                            textAlign: isTargetRTL ? 'right' : 'left',
                            paddingRight: !isTargetRTL ? '2.2rem' : undefined,
                            paddingLeft: isTargetRTL ? '2.2rem' : undefined
                          }}
                          className={isTargetRTL ? 'placeholder-rtl' : 'placeholder-ltr'}
                          ref={el => rightInputRefs.current[index] = el}
                          onFocus={() => {
                            setFocusedInputIdx(index);
                            if (keyboardArmedLang === getKeyboardLangCode(moduleData.to_lang)) {
                              setKeyboardActiveIdx(index);
                            }
                          }}
                          onBlur={e => {
                            setFocusedInputIdx(null);
                            setTimeout(() => {
                              const active = document.activeElement;
                              const isKeyboardKey = active && (
                                active.classList.contains('keyboard-key') ||
                                active.closest('.on-screen-keyboard-overlay')
                              );

                              const isSameLang = rightInputRefs.current.some((input, i) =>
                                input === active && getKeyboardLangCode(moduleData.to_lang) === keyboardArmedLang
                              );

                              if (!isKeyboardKey && !isSameLang) {
                                setKeyboardActiveIdx(null);
                              }
                            }, 100);
                          }}
                        />
                        {focusedInputIdx === index && (
                          <div className="keyboard-icon-absolute" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isTargetRTL ? 'auto' : '0.3rem', left: isTargetRTL ? '0.3rem' : 'auto', zIndex: 2 }}>
                            <button
                              type="button"
                              className="btn-toggle-keyboard subtle-toggle"
                              tabIndex={-1}
                              style={{
                                marginBottom: '0.25rem',
                                fontSize: '1.1rem',
                                background: 'none',
                                border: 'none',
                                color: '#888',
                                padding: '0.15rem 0.5rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '1.8rem',
                                width: '1.8rem',
                              }}
                              aria-label={t('showKeyboard')}
                              onMouseDown={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                const langCode = getKeyboardLangCode(moduleData.to_lang);
                                if (keyboardArmedLang === langCode) {
                                  setKeyboardArmedLang(null);
                                  setKeyboardActiveIdx(null);
                                } else {
                                  setKeyboardArmedLang(langCode);
                                  setKeyboardActiveIdx(index);
                                  rightInputRefs.current[index]?.focus();
                                }
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                <line x1="7" y1="15" x2="7" y2="15" />
                                <line x1="11" y1="15" x2="11" y2="15" />
                                <line x1="15" y1="15" x2="15" y2="15" />
                                <line x1="7" y1="11" x2="7" y2="11" />
                                <line x1="11" y1="11" x2="11" y2="11" />
                                <line x1="15" y1="11" x2="15" y2="11" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      {pair.right.length >= 150 && (
                        <div style={{
                          color: '#d32f2f',
                          fontSize: '0.8rem',
                          marginTop: '4px',
                          textAlign: isTargetRTL ? 'right' : 'left'
                        }}>
                          {t('toLangMaxLength') || 'Translation cannot exceed 150 characters'}
                        </div>
                      )}
                      {pairs.length > 1 || (pairs.length === 1 && (pair.left || pair.right)) ? (
                        <button type="button" onClick={() => handleRemovePair(index)} className="btn-remove-pair" aria-label={t('removeWordPair')}>
                          &times;
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                <button type="button" onClick={handleAddPair} className="btn-add-pair" aria-label={t('addAnotherWord')}>
                  {t('addAnotherWord')}
                </button>
              </div>
              <button onClick={handleManualAdd} className="btn btn-primary btn-confirm-add">{t('addWords')}</button>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="ai-add-section">
              <p>{t('letAIGenerateWords')} <strong>{groupData.name}</strong></p>
              <p className="ai-option-label">{t('numberOfWordsToGenerate')}</p>
              <div className="ai-word-count-selector">
                {[5, 10, 25, 50, 100].map(count => (
                  <button
                    key={count}
                    className={`btn-count ${aiWordCount === count ? 'active' : ''}`}
                    onClick={() => setAiWordCount(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
              <button onClick={handleAiAdd} className="btn btn-primary" disabled={isAddingWithAI}>
                {isAddingWithAI ? t('generating') : t('generateAndAddWords')}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
            {error.includes('Want to learn more?') && (
              <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
                <Link
                  to="/store"
                  className="btn btn-primary"
                  style={{
                    display: 'inline-block',
                    textDecoration: 'none',
                    background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {t('upgradeToPremium')}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {keyboardArmedLang && keyboardActiveIdx !== null && (
        <div ref={keyboardOverlayRef}>
          <OnScreenKeyboard
            languageCode={keyboardArmedLang}
            onInsert={(char) => {
              const input = rightInputRefs.current[keyboardActiveIdx];
              if (input) {
                const start = input.selectionStart;
                const end = input.selectionEnd;
                const currentValue = input.value;

                let newValue;
                if (char === 'backspace') {
                  if (start === end) {
                    newValue = currentValue.slice(0, start - 1) + currentValue.slice(end);
                    input.setSelectionRange(start - 1, start - 1);
                  } else {
                    newValue = currentValue.slice(0, start) + currentValue.slice(end);
                    input.setSelectionRange(start, start);
                  }
                } else if (char === 'space') {
                  newValue = currentValue.slice(0, start) + ' ' + currentValue.slice(end);
                  input.setSelectionRange(start + 1, start + 1);
                } else {
                  newValue = currentValue.slice(0, start) + char + currentValue.slice(end);
                  input.setSelectionRange(start + char.length, start + char.length);
                }

                // Update React state
                handlePairChange(keyboardActiveIdx, 'right', newValue);

                // Keep focus on the input
                input.focus();
              }
            }}
            onClose={() => {
              setKeyboardArmedLang(null);
              setKeyboardActiveIdx(null);
            }}
          />
        </div>
      )}
    </>
  );
};

export default AddMoreWords;
